// The per-task half of the Unassigned tasks heatmap: how many minutes of demand
// the tasks in one unassigned-role group place on one step. Extracted from
// canvas_people_scheduling.js's composeUnassignedHeatmapRow so it can be tested
// in isolation -- the same reason effortDistribution.js exists, and following
// the same rule: no import edges into the canvas-timeline/DataManager graph.
//
// weekdaysBetweenDates is injected rather than imported. The production function
// (canvas_timeline_util.js:1274) reads a feature flag and module-level static day
// data, so importing it would drag the canvas graph in and make this untestable.
// It is NOT interchangeable with effortDistribution.js's getWeekdaysBetweenDatesNew.
//
// Caching, the requiredPersonCount ceiling and the group composition stay with
// the caller. This function only answers "how many minutes".
//
// See PIKA-671 and docs/spec/task-sets/002-capacity-and-heatmap.md.
export const distributeUnassignedTaskMinutes = ({
	items,
	groupId,
	stepStartDate,
	stepEndDate,
	todayDate,
	companyWorkingMinutes,
	weekdaysBetweenDates,
}) => {
	let minutesAllocated = 0;

	for (const item of items) {
		const {task, isHourEstimated, minutesPerEstimationPoint} = item.data;

		// `!task` is load-bearing and must stay IN this condition. `items` is the
		// whole canvas item array -- allocations, placeholders and time
		// registrations included -- and this test is the only thing stopping a
		// taskless item reaching the destructure below.
		//
		// Its position within the condition is not what matters: every operand is
		// OR-ed and the only effect is `continue`, and none of the others
		// dereference `task`. Keep it here; do not rely on it being ahead of the
		// groupId comparison.
		if (
			item.filtered ||
			item.startDate > stepEndDate ||
			item.endDate < stepStartDate - 1 ||
			!task ||
			item.groupId !== groupId
		)
			continue;

		let {timeLeftMinutesWithoutFutureTimeRegs} = task;

		// `!(x > 0)`, not the original `x <= 0`. This and the post-conversion
		// guard below are the only deliberate deviations from a verbatim move,
		// and both are made on PARITY grounds:
		// effortDistribution.js:357 already guards in this stronger form and its
		// comment at :353-356 documents why -- `undefined <= 0` and `NaN <= 0`
		// are both false, so either would fall through into the division below
		// and poison the whole role row via `minutesAllocated += NaN`.
		//
		// What this does NOT buy: an estimate-less task set arrives as `0` -- a
		// primitive double autoboxed by SimpleTaskResponseUtil (:81/:163, :182),
		// never null and never absent -- and `0 <= 0` was already caught by the
		// weak form. No known path brings a task-set instance here as undefined
		// or NaN. This is cheap insurance and alignment with the sibling module,
		// not a task-set bug fix. Do not let a later reader infer otherwise.
		if (!(timeLeftMinutesWithoutFutureTimeRegs > 0)) continue;

		if (!isHourEstimated) {
			timeLeftMinutesWithoutFutureTimeRegs *= minutesPerEstimationPoint;

			// The conversion can reintroduce exactly the NaN the guard above
			// exists to prevent: an undefined or NaN minutesPerEstimationPoint
			// makes the product NaN no matter how sound the remaining estimate
			// was. Guarding only the input would leave the guard one line short
			// of its stated purpose.
			//
			// This rests on the same "no known path reaches it" footing as the
			// guard itself -- and note effortDistribution.js offers no parity
			// cover here either way, because it has no points conversion at all.
			if (!(timeLeftMinutesWithoutFutureTimeRegs > 0)) continue;
		}

		const isInThePast = item.endDate < todayDate;

		//If task is completely in the past or completely in the future, use normal task start date for calculation
		//If task is partially in the past and partially in the future, use today as the starting date
		const calculationStartDate = isInThePast ? item.startDate : Math.max(todayDate, item.startDate);

		//How many weekdays are in the task (ex: 1 monday, 1 tuesday, 1 wednesday)
		const taskTotalWeekdays = weekdaysBetweenDates(calculationStartDate, item.endDate);

		//Weekdays which are within the current loop period
		const taskLoopWeekdays = weekdaysBetweenDates(
			Math.max(calculationStartDate, stepStartDate),
			Math.min(item.endDate, stepEndDate)
		);

		//Total length of task in days
		const taskTotalDayCount = taskTotalWeekdays.reduce((total, weekdayCount) => {
			return total + weekdayCount;
		}, 0);

		//Total length of task in days minus days on which the company has 0 work minutes
		const taskWorkingDayCount = taskTotalWeekdays.reduce((total, weekdayCount, index) => {
			return total + (companyWorkingMinutes[index] ? weekdayCount : 0);
		}, 0);

		//Number of days within current loop period
		const taskLoopTotalDayCount = taskLoopWeekdays.reduce((total, weekdayCount) => {
			return total + weekdayCount;
		}, 0);

		//Number of days within current loop period minus days on which the company has 0 work minutes
		const taskLoopWorkingDayCount = taskLoopWeekdays.reduce((total, weekdayCount, index) => {
			return total + (companyWorkingMinutes[index] ? weekdayCount : 0);
		}, 0);

		//If there are any working days during the task's period, spread timeLeft evenly among working days only
		//If there are no working days during the task's period, spread timeLeft evenly among all days
		if (taskWorkingDayCount) {
			minutesAllocated += (timeLeftMinutesWithoutFutureTimeRegs * taskLoopWorkingDayCount) / taskWorkingDayCount;
		} else {
			minutesAllocated += (timeLeftMinutesWithoutFutureTimeRegs * taskLoopTotalDayCount) / taskTotalDayCount;
		}
	}

	return minutesAllocated;
};
